import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'

/* Layouts */
import AppLayout from '@/lib/layouts/AppLayout.vue'
import DefaultLayout from '@/lib/layouts/DefaultLayout.vue'
import AuthLayout from '@/lib/layouts/AuthLayout.vue'

/* UI */
import { SplashScreen } from '@/lib/components'

/* Stores */
import { useUserStore } from '@/lib/stores'

/* Services */
import {
  $accountService,
  useMarketingService,
  useMhcService,
  useNotificationService,
} from '@/lib/services'

/* Config & Utils */
import { AppConfig } from '@/lib/config'
import { markShellReady } from './lib/config/spa/shell-ready'
import { createSplitFactory } from './lib/composable/split.io/splitClient'
import { verifyAccountCluster } from './lib/utils/utils'
import {
  fetchAccounts,
  fetchAccountIdFromUrl,
  findAccountFromAccounts,
} from './lib/utils/accounts'
import { getCombinedFeatures } from './lib/utils/features'
import { getAccountSwitchPath, isActiveAccountPath } from './lib/utils/shell'
/* Auth */
import keycloak from './lib/plugins/keycloak-config'

/* Routing */
import getDynamicRoutes from './lib/router/updateRoutes'

/* ============================================================================
 * STEP 0: LAYOUT REGISTRY
 * ============================================================================
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

/* ============================================================================
 * IMPORTS
 * ============================================================================
 */
const layouts = {
  app: AppLayout,
  default: DefaultLayout,
  auth: AuthLayout,
}

const selectedLayout = ref<'app' | 'default' | 'auth'>('app')
const isAccountReady = ref(false)

const router = useRouter()

/* ============================================================================
 * STEP 1: SHELL BOOTSTRAP (ENTRY POINT)
 * ============================================================================
 */
onMounted(async () => {
  const userStore = useUserStore()

  // Disable sales navigation by default (enabled later if required)
  userStore.setSaleNavigation(false)
  

  /* --------------------------------------------------------------------------
   * STEP 1.1: AUTHENTICATION GUARD
   * --------------------------------------------------------------------------
   */
  if (!keycloak || !keycloak.authenticated) {
    selectedLayout.value = 'auth'
    isAccountReady.value = true
    return
  }

  /* --------------------------------------------------------------------------
    * STEP 1.5: FAST ACCOUNT VALIDATION
    * --------------------------------------------------------------------------
    * If accountId in URL already matches the active account,
    * some bootstrap steps can be executed earlier to reduce delay.
  */
  let skipAccountBootstrap = false

  if (userStore.getAccountId &&
    isActiveAccountPath(window.location.pathname, userStore.getAccountId.toString())
  ) {
    skipAccountBootstrap = true
    verifyAccountClusterSafe(userStore.getDefaultAccount)
    registerDynamicRoutes(userStore, router)
  }

  /* --------------------------------------------------------------------------
   * STEP 2: LOAD USER PROFILE
   * --------------------------------------------------------------------------
   */
  try {
    await userStore.getUserData()
  } catch (err) {
    console.error('Failed to fetch user data:', err)
  }

  /* --------------------------------------------------------------------------
   * STEP 3: FETCH LINKED ACCOUNTS
   * --------------------------------------------------------------------------
   */
  let accounts: any[] = []

  try {
    accounts = await fetchAccounts()
  } catch (err) {
    const message = encodeURIComponent(
      'Something went wrong.'
    )

    window.location.replace(
      `${AppConfig.IDENTITY_BASE_URL}/#/dashboard?code=400&message=${message}`
    )
    return;
  }

  /* --------------------------------------------------------------------------
   * STEP 4: FETCH PRODUCT-ONLY ACCOUNTS (NETO / MHC)
   * --------------------------------------------------------------------------
   */
  try {
    await $accountService.fetchNetoAccount({
      params: { filters: 'neto/tenancies' },
    })

    const netoAccounts = $accountService.netoAccounts.value || []
    userStore.setSingleProductAccess('neto', netoAccounts.length > 0)
    userStore.setNetoStores(netoAccounts)
  } catch (err) {
    userStore.setSingleProductAccess('neto', false)
    console.error('Failed to fetch Neto accounts:', err)
  }

  try {
    const mhcService = useMhcService()
    await mhcService.findAll()

    userStore.setSingleProductAccess('mhc', mhcService.items.value.length > 0)
    userStore.setMhcStores(mhcService.items.value)
  } catch (err) {
    userStore.setSingleProductAccess('mhc', false)
    console.error('Failed to fetch MHC accounts:', err)
  }

  /* --------------------------------------------------------------------------
   * STEP 5: NO ACCOUNT FALLBACK
   * --------------------------------------------------------------------------
   */
  if (!accounts || accounts.length === 0) {
    redirectToOtherProduct(userStore)
    return
  }

  /* ============================================================================
   * STEPS BELOW ARE GUARANTEED TO HAVE ACCOUNTS
   * ============================================================================
   */

  /* --------------------------------------------------------------------------
   * STEP 6: RESOLVE ACTIVE ACCOUNT
   * --------------------------------------------------------------------------
   */
  const parsedAccountId = Number.parseInt(fetchAccountIdFromUrl())

  const resolvedAccountId =
    Number.isFinite(parsedAccountId) && parsedAccountId > 0
      ? parsedAccountId
      : userStore.getAccountId ||
        userStore.getDefaultAccountId ||
        accounts[0].id

  const account =
    (await findAccountFromAccounts(accounts, resolvedAccountId)) ||
    (await findAccountFromAccounts(accounts, userStore.getDefaultAccountId)) ||
    accounts[0]

  /* --------------------------------------------------------------------------
   * STEP 7: VERIFY ACCOUNT CLUSTER
   * --------------------------------------------------------------------------
   */
   if (!skipAccountBootstrap) {
      try {
        verifyAccountClusterSafe(account)
      } catch (err) {
        console.error('Account cluster verification failed:', err)
      }
    }

  /* --------------------------------------------------------------------------
   * STEP 8: PERSIST ACCOUNT CONTEXT
   * --------------------------------------------------------------------------
   */
  try {
    userStore.setAccountId(account.id)
    userStore.setDefaultAccount(account)
    userStore.setAccounts(accounts)
    userStore.setProductAccess(account)
  } catch (err) {
    console.error('Failed to persist account state:', err)
  }

  /* --------------------------------------------------------------------------
   * STEP 9: FETCH PACKAGE & TRIAL FEATURES
   * --------------------------------------------------------------------------
   */
  try {
    const response = await $accountService.fetchPackageFeatures()

    userStore.setTrailPackageFeatures(response.data.data)
    userStore.setPackageFeatures(getCombinedFeatures(response.data))

    setUserAccessBasedOnPackage(response.data.data, userStore)

    // If no core products remain → redirect
    if (
      !userStore.getProductAccess('marketing') &&
      !userStore.getProductAccess('service') &&
      !userStore.getProductAccess('commerce')
    ) {
      if(accounts.length > 1){
        invalidAccount(account.id)
        return
      }
      redirectToOtherProduct(userStore)
      return
    }
  } catch (err) {
    invalidAccount(account.id)
    console.error('Failed to fetch package features:', err)
    return
  }

  /* --------------------------------------------------------------------------
   * STEP 10: INITIALIZE FEATURE FLAGS (SPLIT.IO)
   * --------------------------------------------------------------------------
   */
  try {
    await createSplitFactory()
  } catch (err) {
    console.error('Split.io initialization failed:', err)
  }

  /* --------------------------------------------------------------------------
   * STEP 11: FETCH ROLES & MARKETING PACKAGE INFO
   * --------------------------------------------------------------------------
   */
  try {
    const marketingService = useMarketingService()

    await Promise.all([
      $accountService.fetchRoles(),
      marketingService.fetchAccountInfo(),
    ])

    userStore.setRoles($accountService.roles.value)
  } catch (err) {
    userStore.setSingleProductAccess('marketing', false)
    console.error('Failed to fetch roles or marketing packages:', err)
    // set roles to empty array to avoid any access checks based on roles
    userStore.setRoles({ roles: [] })
    if (!userStore.getProductAccess('commerce')) {
      invalidAccount(account.id)
      return
    }
  }

  /* --------------------------------------------------------------------------
   * STEP 12: FETCH NOTIFICATIONS (MARKETING OR COMMERCE)
   * --------------------------------------------------------------------------
   */
  if (
    (
      userStore.getProductAccess('marketing') &&
      userStore.getProductAccessOnRoles('marketing')
    ) ||
    userStore.getProductAccess('commerce')
  ) {
    try {
      useNotificationService.fetchNotifications()
    } catch (err) {
      console.error('Failed to fetch notifications:', err)
    }
  }

  /* --------------------------------------------------------------------------
   * STEP 13: DYNAMIC ROUTE REGISTRATION
   * --------------------------------------------------------------------------
   */
  if (!skipAccountBootstrap) {
    registerDynamicRoutes(userStore, router)
  }

  /* --------------------------------------------------------------------------
   * STEP 14: FINALIZE SHELL BOOTSTRAP
   * --------------------------------------------------------------------------
   */
  try {
    markShellReady()
    isAccountReady.value = true

    import('./lib/config/spa/single-spa-config').catch(err =>
      console.error('Failed to load single-spa config:', err)
    )
  } catch (err) {
    console.error('Shell ready finalization failed:', err)
    isAccountReady.value = true
  }
})

/* ============================================================================
 * HELPERS
 * ============================================================================
 */

/**
 * Redirects user when account is invalid or inaccessible
 */
const invalidAccount = (id: string) => {
  isAccountReady.value = true
  selectedLayout.value = 'default'

  router.push({
    path: '/select-account',
    query: { accountId: id },
  })
}

/**
 * Package → Product mapping
 */
const PACKAGE_TO_PRODUCT_MAP: Record<string, string> = {
  mmc: 'marketing',
  msc: 'service',
  mcc: 'commerce',
}

/**
 * Disable product access when corresponding package is missing
 */
const setUserAccessBasedOnPackage = (
  packages: Record<string, any>,
  userStore: any
) => {
  if (!packages || typeof packages !== 'object') return

  Object.entries(PACKAGE_TO_PRODUCT_MAP).forEach(
    ([packageKey, productKey]) => {
      const pkg = packages[packageKey]

      // Empty or missing package disables access
      if (!pkg || !pkg.package_name) {
        userStore.setSingleProductAccess(productKey, false)
      }
    }
  )
}

/**
 * Redirect user to alternate product dashboards
 * when no account-based products are available
 */
const redirectToOtherProduct = (userStore: any) => {
  if (userStore.getProductAccess('neto')) {
    router.push('/neto/dashboard')
    isAccountReady.value = true
    return
  }

  if (userStore.getProductAccess('mhc')) {
    router.push('/merchandising/dashboard')
    isAccountReady.value = true
    return
  }

  const message = encodeURIComponent(
    'No linked account found for this user. Kindly contact Admin.'
  )

  window.location.replace(
    `${AppConfig.IDENTITY_BASE_URL}/#/dashboard?code=400&message=${message}`
  )
}

/**
 * Safely verifies if the resolved account belongs to the correct cluster.
 * This function wraps the cluster verification logic with error handling
 * to prevent bootstrap failures if verification fails.
 *
 * @param account - resolved account object
 */
 const verifyAccountClusterSafe = (account: any) => {
  try {
    verifyAccountCluster(account)
  } catch (err) {
    console.error('Account cluster verification failed:', err)
  }
}


/**
 * Registers dynamic routes for the application and ensures
 * the router navigates to the correct account-scoped path.
 *
 * Responsibilities:
 * - Inject dynamic routes returned by getDynamicRoutes
 * - Prevent duplicate route registrations
 * - Ensure accountId in URL matches active account
 *
 * @param userStore - user store instance
 * @param router - vue router instance
 */
const registerDynamicRoutes = (userStore: any, router: any) => {
  const dynamicRoutes = getDynamicRoutes(userStore, router)

  dynamicRoutes.forEach(route => {
    if (route.name && !router.hasRoute(route.name)) {
      router.addRoute(route)
    }
  })

  // Redirect root to dashboard
  if (router.currentRoute.value.path === '/') {

    router.replace(`/accounts/${userStore.getAccountId}/dashboard`)

  } else {

    const currentPath = router.currentRoute.value.path

    const newPath = getAccountSwitchPath(
      currentPath,
      userStore.getAccountId.toString()
    )

    router.replace({
      path: newPath,
      query: router.currentRoute.value.query,
      hash: router.currentRoute.value.hash
    })

  }

}

return (_ctx: any,_cache: any) => {
  const _component_v_app = _resolveComponent("v-app")!

  return (_openBlock(), _createBlock(_component_v_app, { class: "maropost-app" }, {
    default: _withCtx(() => [
      (!isAccountReady.value)
        ? (_openBlock(), _createBlock(_unref(SplashScreen), { key: 0 }))
        : (_openBlock(), _createBlock(_resolveDynamicComponent(layouts[selectedLayout.value as keyof typeof layouts]), { key: 1 }))
    ]),
    _: 1
  }))
}
}

})